/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { isExternalLink } from '@coursera-web/cds-labs';
import type { ImageType } from '@coursera-web/cds-labs';

import Imgix from 'js/components/Imgix';
import { processImage } from 'js/lib/imgix';
import { isAuthenticatedUser } from 'js/lib/user';

import type { ButtonProps, IconButtonProps } from '@coursera/cds-core';
import { Button, Hidden, IconButton, Link, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';
import type { Button as EventingButton, MerchandisingModule } from '@coursera/event-pulse-types';
import type { EventData } from '@coursera/event-pulse/react';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import PrePurchaseUpsellCourseraPlusModal from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellCourseraPlusModal';
import { useCourseraPlusSubscriptionFreeTrials } from 'bundles/coursera-plus-common/hooks/useCourseraPlusSubscriptionFreeTrials';

import CplusLogo from 'images/unified-common/coursera-plus-logo-invert.svg';
import BgImage from 'images/unified-common/cplus-upsell-card-image.png';
import BgImageSmDown from 'images/unified-common/cplus-upsell-small-banner-smdown.png';

import _t from 'i18n!nls/unified-common';

const CARD_MIN_HEIGHT = 275;
const TEXT_SIZE_CARD_HEIGHT_CUTOFF = 300;
const IMAGE_HIDE_CARD_HEIGHT_CUTOFF = 335;
const IMAGE_HIDE_CARD_WIDTH_CUTOFF = 225;
const BUTTON_WIDTH_POSITION_UNSET = 240;
const IMAGE_Z_INDEX = 1;

type MobileBreakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

type Props = {
  title: string;
  mobileBreakpoint?: MobileBreakpoint;
  eventingData?: {
    modulePlacement?: 'HIGH' | 'LOW';
    index?: number;
  };
  forceOpenInNewTab?: boolean;
};

type BaseButton = {
  text: string;
  buttonName: EventingButton['name'];
};

type LinkButton = {
  href: string;
  ariaLabel?: string;
} & BaseButton;

type ActionButton = {
  onClick: () => void;
  ariaLabel?: string;
} & BaseButton;

type ImageDisiplayConfig = {
  displayUnderButton?: boolean;
};

type CampaignCardProps = {
  title: string;
  eyebrowImage?: ImageType;
  cta?: ActionButton | LinkButton;
  description?: string;
  image: ImageType;
  imageDisplayConfig?: ImageDisiplayConfig;
  mobileBreakpoint: MobileBreakpoint;
  eventingData: {
    merchModule: MerchandisingModule;
  };
  forceOpenInNewTab?: boolean;
};

const getDesktopBreakpoint = (mobileBreakpoint: MobileBreakpoint) => {
  switch (mobileBreakpoint) {
    case 'xs':
      return 'sm';
    case 'sm':
      return 'md';
    case 'md':
      return 'lg';
    case 'lg':
      return 'xl';
    default:
      return undefined;
  }
};

const styles = {
  card: (mobileBreakpoint: MobileBreakpoint) => css`
    border-radius: var(--cds-spacing-200) !important;
    text-decoration: none !important;
    background-color: var(--cds-color-emphasis-primary-background-strong);
    max-height: 100%;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: end;
    justify-content: space-between;
    position: relative;
    container-type: size;

    ${breakpoints.down(mobileBreakpoint)} {
      container-type: unset;
      display: grid;
      grid-template-columns: 1fr auto;
      height: unset;
    }
  `,
  cardDesktop: css`
    min-height: ${CARD_MIN_HEIGHT}px;
  `,
  cardClickable: css`
    cursor: pointer;
    transition: 0.3s cubic-bezier(0, 0, 0.5, 1);

    &:focus {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 1px;
    }

    &:hover {
      box-shadow: 0 0 40px -8px var(--cds-color-darken-300), 0 0 24px -16px var(--cds-color-darken-300);
      transform: scale3d(1.03, 1.03, 1.08) translate3d(0.1rem, -0.25rem, 20rem);
    }
  `,
  content: (mobileBreakpoint: MobileBreakpoint) => css`
    padding: var(--cds-spacing-400) var(--cds-spacing-300);
    padding-bottom: 0;
    align-self: flex-start;

    ${breakpoints.down(mobileBreakpoint)} {
      padding: var(--cds-spacing-200);
      padding-right: var(--cds-spacing-50);

      [dir='rtl'] & {
        padding: var(--cds-spacing-200);
        padding-left: var(--cds-spacing-50);
      }
    }
  `,
  contentDesktop: css`
    /* Scale down heading text on desktop card when the card is small.
       This prevents the CTA from being pushed out of view */
    @container (max-height: ${TEXT_SIZE_CARD_HEIGHT_CUTOFF}px) {
      & > div {
        margin-bottom: var(--cds-spacing-200);
      }

      & > h2 {
        font-size: var(--cds-font-size-300);
        line-height: var(--cds-line-height-400);
      }

      & img {
        height: 14px;
      }
    }
  `,
  buttonWithImageUnder: css`
    position: absolute;
    z-index: ${IMAGE_Z_INDEX + 1};

    /* Unset button position when container width is narrow on RTL */
    @container (max-width: ${BUTTON_WIDTH_POSITION_UNSET}px) {
      [dir='rtl'] & {
        position: unset;
      }
    }
  `,
  eyebrowImage: (mobileBreakpoint: MobileBreakpoint) => css`
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down(mobileBreakpoint)} {
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  decorationImage: (imageUrl: string) => css`
    max-width: 75%;
    background-image: url(${imageUrl});
    background-position: bottom right;
    background-repeat: no-repeat;
    background-size: contain;
    width: 100%;
    height: 100%;
    z-index: ${IMAGE_Z_INDEX};

    [dir='rtl'] & {
      transform: scaleX(-1);
      background-position-x: left;
    }

    /* Hide the image when the container is small, otherwise the image is comically small */
    @container (max-height: ${IMAGE_HIDE_CARD_HEIGHT_CUTOFF}px) {
      background-image: none;
    }

    /* Hide image when container width is narrow on RTL */
    @container (max-width: ${IMAGE_HIDE_CARD_WIDTH_CUTOFF}px) {
      [dir='rtl'] & {
        background-image: none;
      }
    }
  `,
  title: (mobileBreakpoint: MobileBreakpoint) => css`
    ${breakpoints.down(mobileBreakpoint)} {
      font-size: 20px;
    }
  `,
  titleMargin: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  descriptionMargin: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  iconButtonContainer: (imageUrl: string, mobileBreakpoint: MobileBreakpoint) => css`
    ${breakpoints.down(mobileBreakpoint)} {
      background-image: url(${imageUrl});
      background-position: left;
      background-size: cover;
      padding-left: var(--cds-spacing-400);
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 100%;
      padding-right: var(--cds-spacing-200);

      [dir='rtl'] & {
        transform: scaleX(-1);
        padding-right: var(--cds-spacing-400);
        padding-left: var(--cds-spacing-200);

        > button {
          transform: scaleX(-1);
        }
      }
    }
  `,
};

const getLinkButtonProps = (
  cta: LinkButton,
  eventingData: {
    merchModule: MerchandisingModule;
  },
  buttonName: EventingButton['name'],
  forceOpenInNewTab?: boolean
) => {
  let target: string | undefined;
  let rel: string | undefined;
  const clickData: EventData<'click_merchandising_module'> = {
    button: {
      name: buttonName,
    },
    merchandisingModule: eventingData.merchModule,
  };

  try {
    const isExternal = isExternalLink(cta.href);
    if (isExternal) {
      target = '_blank';
      rel = 'noopener noreferrer';
      clickData.button = {
        name: buttonName,
        linkType: 'external',
        linkURL: cta.href,
      };
    } else {
      clickData.button = {
        name: buttonName,
        linkType: 'internal',
        linkURL: cta.href,
      };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    // if the URL is relative, an error is thrown when calling isExternalLink
    target = forceOpenInNewTab ? '_blank' : undefined;
    clickData.button = {
      name: buttonName,
      linkType: 'internal',
      linkURL: cta.href,
    };
  }

  return {
    target,
    rel,
    clickData,
  };
};

/**
 * This component is not yet ready for general use, so it is not exported or separated into its own file.
 */
const CampaignCard = ({
  eyebrowImage,
  title,
  cta,
  description,
  image,
  imageDisplayConfig,
  mobileBreakpoint,
  eventingData,
  forceOpenInNewTab,
}: CampaignCardProps) => {
  const desktopMediaQuery = useMediaQuery(breakpoints.up(getDesktopBreakpoint(mobileBreakpoint) ?? 'xl'));
  const isDesktop = mobileBreakpoint === 'xl' ? false : desktopMediaQuery;
  const addTitleMarginBottom = (Boolean(description) || Boolean(cta)) && isDesktop;
  const addDescriptionmarginBottom = Boolean(cta) && isDesktop;
  const track = useTracker();
  const visibilityTrackerRef = useVisibilityTracker('view_merchandising_module', {
    merchandisingModule: eventingData.merchModule,
  });

  const hiddenOnMobile: Partial<React.ComponentProps<typeof Hidden>> = React.useMemo(() => {
    switch (mobileBreakpoint) {
      case 'xl':
        return {
          xlDown: true,
        };
      case 'lg':
        return {
          lgDown: true,
        };
      case 'md':
        return {
          mdDown: true,
        };
      case 'sm':
        return {
          smDown: true,
        };
      case 'xs':
        return { only: ['xs'] };
      default:
        return { only: ['xs'] };
    }
  }, [mobileBreakpoint]);

  const hiddenOnDesktop: Partial<React.ComponentProps<typeof Hidden>> = React.useMemo(() => {
    switch (mobileBreakpoint) {
      case 'xl':
        return { only: [] };
      case 'lg':
        return { only: ['xl'] };
      case 'md':
        return {
          lgUp: true,
        };
      case 'sm':
        return {
          mdUp: true,
        };
      case 'xs':
        return {
          smUp: true,
        };
      default:
        return {
          smUp: true,
        };
    }
  }, [mobileBreakpoint]);

  let desktopCta: React.ReactNode;
  let mobileCta: React.ReactNode;

  const buttonProps: Partial<ButtonProps> = {
    variant: 'primaryInvert',
    size: 'small',
    'aria-label': cta?.ariaLabel ?? cta?.text ?? '',
  };
  const iconButtonProps: IconButtonProps = {
    variant: 'secondary',
    size: 'small',
    tooltip: cta?.text ?? '',
    'aria-label': cta?.ariaLabel ?? cta?.text ?? '',
    icon: <ChevronNextIcon size="small" />,
  };

  if (cta && 'href' in cta) {
    const { clickData, rel, target } = getLinkButtonProps(cta, eventingData, 'c_plus_learn_more', forceOpenInNewTab);
    const trackClick = () => track('click_merchandising_module', clickData);
    desktopCta = (
      <Button
        component="a"
        href={cta.href}
        css={imageDisplayConfig?.displayUnderButton ? styles.buttonWithImageUnder : undefined}
        onClick={(e: React.MouseEvent | React.KeyboardEvent) => {
          trackClick();
          e.stopPropagation();
        }}
        rel={rel}
        target={target}
        {...buttonProps}
      >
        {cta.text}
      </Button>
    );
    mobileCta = (
      <IconButton
        component="a"
        href={cta.href}
        onClick={(e: React.MouseEvent | React.KeyboardEvent) => {
          trackClick();
          e.stopPropagation();
        }}
        rel={rel}
        target={target}
        {...iconButtonProps}
      />
    );
  } else if (cta && 'onClick' in cta) {
    const trackClick = () => {
      track('click_merchandising_module', {
        merchandisingModule: eventingData.merchModule,
        button: {
          name: 'start_free_trial',
        },
      });
    };
    desktopCta = (
      <Button
        onClick={(e: React.MouseEvent | React.KeyboardEvent) => {
          trackClick();
          cta.onClick();
          e.stopPropagation();
        }}
        css={imageDisplayConfig?.displayUnderButton ? styles.buttonWithImageUnder : undefined}
        {...buttonProps}
      >
        {cta.text}
      </Button>
    );
    mobileCta = (
      <IconButton
        onClick={(e: React.MouseEvent | React.KeyboardEvent) => {
          trackClick();
          cta.onClick();
          e.stopPropagation();
        }}
        {...iconButtonProps}
      />
    );
  }

  const renderContent = () => (
    <React.Fragment>
      <div css={[styles.content(mobileBreakpoint), isDesktop ? styles.contentDesktop : undefined]}>
        {eyebrowImage?.url && (
          <div css={styles.eyebrowImage(mobileBreakpoint)}>
            <Imgix
              src={eyebrowImage.url}
              alt={eyebrowImage.description ?? ''}
              height={eyebrowImage.height ?? undefined}
            />
          </div>
        )}
        <Typography2
          variant="titleSmall"
          component="h2"
          color="invertBody"
          css={[styles.title(mobileBreakpoint), addTitleMarginBottom ? styles.titleMargin : undefined]}
        >
          {title}
        </Typography2>
        <Hidden {...hiddenOnMobile}>
          {description && (
            <Typography2
              variant="bodyPrimary"
              component="p"
              color="invertBody"
              css={addDescriptionmarginBottom ? styles.descriptionMargin : undefined}
            >
              {description}
            </Typography2>
          )}
        </Hidden>
        <Hidden {...hiddenOnMobile}>{desktopCta}</Hidden>
      </div>

      {isDesktop && image.url && <div css={styles.decorationImage(processImage(image.url))} />}

      <Hidden {...hiddenOnDesktop} css={styles.iconButtonContainer(processImage(image.url) ?? '', mobileBreakpoint)}>
        {mobileCta}
      </Hidden>
    </React.Fragment>
  );

  if (!cta) {
    return (
      <div css={[styles.card(mobileBreakpoint), isDesktop ? styles.cardDesktop : undefined]}>{renderContent()}</div>
    );
  }

  if ('href' in cta) {
    const { target, rel, clickData } = getLinkButtonProps(cta, eventingData, 'merchandising_module', forceOpenInNewTab);
    const trackClick = () => {
      track('click_merchandising_module', clickData);
    };
    return (
      <Link
        href={cta.href}
        onClick={() => {
          trackClick();
        }}
        target={target}
        rel={rel}
        css={[styles.card(mobileBreakpoint), styles.cardClickable]}
        ref={visibilityTrackerRef as React.MutableRefObject<HTMLAnchorElement | null>}
      >
        {renderContent()}
      </Link>
    );
  }

  const handleClick = () => {
    cta.onClick();
    track('click_merchandising_module', {
      merchandisingModule: eventingData.merchModule,
      button: {
        name: 'merchandising_module',
      },
    });
  };

  return (
    <div
      css={[styles.card(mobileBreakpoint), styles.cardClickable]}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          handleClick();
          e.preventDefault();
        }
      }}
      role="button"
      tabIndex={0}
      ref={visibilityTrackerRef as React.MutableRefObject<HTMLDivElement | null>}
    >
      {renderContent()}
    </div>
  );
};

/**
 *
 * @property mobileBreakpoint: Starting at this breakpoint and at all lower breakpoints, the mobile design will be displayed
 * @returns
 */
const CourseraPlusUpsellCard = ({ title, mobileBreakpoint = 'sm', eventingData, forceOpenInNewTab }: Props) => {
  const isMobile = useMediaQuery(breakpoints.down(mobileBreakpoint));
  const isUserLoggedIn = isAuthenticatedUser();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { userHasCourseraPlusFreeTrial: userIsEligibleForFreeTrial } = useCourseraPlusSubscriptionFreeTrials();

  const cta: ActionButton | LinkButton = isUserLoggedIn
    ? {
        text: _t('Start free trial'),
        buttonName: 'start_free_trial',
        onClick: () => setIsModalOpen(true),
      }
    : {
        text: _t('Learn more'),
        buttonName: 'c_plus_learn_more',
        href: '/courseraplus',
        ariaLabel: _t('Learn more about Coursera Plus'),
      };

  if (isAuthenticatedUser() && !userIsEligibleForFreeTrial) {
    cta.text = _t('Renew subscription');
  }

  const cplusLogoEyebrow: ImageType = {
    url: CplusLogo,
    height: isMobile ? 12 : 18,
    useHeightPixelValue: true,
  };

  const image: ImageType = {
    url: isMobile ? BgImageSmDown : BgImage,
  };

  return (
    <React.Fragment>
      <PrePurchaseUpsellCourseraPlusModal
        handleClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        hasCourseraPlusFreeTrial={userIsEligibleForFreeTrial}
      />
      <CampaignCard
        title={title}
        cta={cta}
        eyebrowImage={cplusLogoEyebrow}
        image={image}
        imageDisplayConfig={{ displayUnderButton: true }}
        mobileBreakpoint={mobileBreakpoint}
        forceOpenInNewTab={forceOpenInNewTab}
        eventingData={{
          merchModule: {
            moduleType: 'coursera_plus_upsell_card',
            heading: title,
            hasImage: true,
            index: eventingData?.index,
          },
        }}
      />
    </React.Fragment>
  );
};

export default CourseraPlusUpsellCard;
